import { 
    useState, 
    useEffect, 
} from 'react';
import {
    useTheme,
    Box,
    Button,
    Modal,
    FormGroup,
    FormControlLabel,
    Checkbox,
    TextField,
    Typography,
    IconButton,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { PasswordField } from '../../general';
import { useEffectExceptOnMount } from '../../../hooks';
import { generateRandomPasscode } from '../../../utils';

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: {xs: '70vw', sm: 400},
    bgcolor: 'background.paper',
    border: '2px solid #1e2229',
    borderRadius: '20px',
    boxShadow: 24,
    p: 3,
};

const EditModal = ({ open, setOpen, conversion, handleEdit, advanced, setAdvancedSafe, fetchPasscode }) => {

    const theme = useTheme();

    const [newPasscodeLoading, setNewPasscodeLoading] = useState(false);
    const [newPasscode, setNewPasscode] = useState('');
    
    // Passcode edit field visibility
    const [showPasscodeEdit, setShowPasscodeEdit] = useState(false);
    const handleClickShowPasscodeEdit = () => setShowPasscodeEdit(show => !show);
    //

    useEffectExceptOnMount(() => {
        setAdvancedSafe({
            passcode: newPasscode,
        })
    }, [newPasscode]);
    
    useEffect(() => {
        // Passcode recovery
        fetchPasscode(setNewPasscode, setNewPasscodeLoading);
    }, []);

    const close = () => {
        setOpen(false);
        setShowPasscodeEdit(false);
        setAdvancedSafe({
            maxRedirects: conversion.maxRedirects,
            revealable: conversion.revealable,
            isDead: conversion.isDead,
            passcode: conversion.passcode,
        })
        fetchPasscode(setNewPasscode, setNewPasscodeLoading);
    }
    
    const generatePasscode = () => setNewPasscode(generateRandomPasscode());

    return (
        <Modal
            open={open}
            onClose={close}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
        >
            <Box sx={{...style, border: theme.palette.mode === 'dark' ? '2px solid #1e2229': '2px solid #AAB4BE'}}>
                <Box sx={{display: 'flex', flexDirection: 'column', width: '100%', gap: 2}}>
                    <Box>
                        <Box sx={{display: 'flex', alignItems: 'baseline'}}>
                            <Box 
                                className='h3-style'
                                sx={{
                                    overflowX: 'auto',
                                    textDecoration: 'underline',
                                    whiteSpace: 'nowrap',
                                    "&::-webkit-scrollbar": {
                                        height: "5px",
                                    },
                                    "&::-webkit-scrollbar-thumb": {
                                        backgroundColor: theme.palette.mode === "light" ? "#c1c1c1" : "#6b6b6b",
                                        borderRadius: "4px",
                                    },
                                }}
                            >
                                Edit {conversion.code}
                            </Box>
                            <Box sx={{marginLeft: 'auto'}}>
                                <IconButton 
                                    sx={{cursor: 'pointer'}}
                                    onClick={close}
                                >
                                    <Close fontSize='small' />
                                </IconButton>
                            </Box>
                        </Box>
                        <Box>
                            <FormGroup>
                                <FormControlLabel control={
                                    <Checkbox 
                                        checked={advanced.revealable} 
                                        onChange={e => setAdvancedSafe({ revealable: e.target.checked })}
                                    />
                                } label="Reveal source link" />
                                {
                                    conversion.markers.isActive &&
                                    <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
                                        <FormControlLabel control={
                                            <Checkbox 
                                                checked={advanced.isDead} 
                                                onChange={e => setAdvancedSafe({ isDead: e.target.checked })}
                                            />
                                        } label="Dead" />
                                        <Box sx={{display: 'flex', alignItems: 'flex-end'}}>
                                            <Box>
                                                <Typography valiant="h1" component="div">
                                                    Maximum number of redirects: 
                                                </Typography>
                                            </Box>
                                            <Box sx={{width: '100%', display: 'flex', justifyContent: 'flex-end'}}>
                                                <TextField
                                                    id="outlined-number-redirects"
                                                    label="Redirects"
                                                    type="number"
                                                    InputLabelProps={{
                                                        shrink: true,
                                                    }}
                                                    variant="standard" 
                                                    sx={{ml: 3, width: {sm: '200px'}}}
                                                    value={advanced.maxRedirects}
                                                    onChange={e => {
                                                        const val = e.target.value;
                                                        setAdvancedSafe({
                                                            maxRedirects: !!val ? val : null
                                                        });
                                                    }}
                                                />
                                            </Box>
                                        </Box>
                                        <Box sx={{display: 'flex', alignItems: 'flex-end'}}>
                                            <Box>
                                                <Typography valiant="h1" component="div">
                                                    Passcode: 
                                                </Typography>
                                            </Box>
                                            <Box sx={{width: '100%', display: 'flex', justifyContent: 'flex-end'}}>
                                                <PasswordField
                                                    label='Passcode'
                                                    value={newPasscode}
                                                    handleChange={e => setNewPasscode(e.target.value)}
                                                    show={showPasscodeEdit} 
                                                    handleShow={handleClickShowPasscodeEdit}
                                                    variant="standard" 
                                                    style={{width: {sm: '200px'}}}
                                                    loading={newPasscodeLoading}
                                                    loaderSize={18}
                                                    generateFunc={generatePasscode}
                                                    iconFontSize='small'
                                                />
                                            </Box>
                                        </Box>
                                    </Box>
                                }
                            </FormGroup>
                        </Box>
                    </Box>
                    <Box sx={{ml: 'auto', display: 'flex', gap: 1}}>
                        <Button variant="outlined" sx={{textTransform: "none", fontWeight: 'bold', borderRadius: '30px'}} onClick={close}>Cancel</Button>
                        <Button variant="contained" sx={{textTransform: "none", fontWeight: 'bold', borderRadius: '30px'}} onClick={handleEdit}>Modify</Button>
                    </Box>
                </Box>
            </Box>
        </Modal>
    );
}

export default EditModal;